import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import StickyMobileCTA from '../components/StickyMobileCTA';

// ── Sub-components ────────────────────────────────────────────────────────────

const contactItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

interface ContactItemProps {
  label: string;
  value: React.ReactNode;
  href?: string;
}

const ContactItem: React.FC<ContactItemProps> = ({ label, value, href }) => (
  <motion.div variants={contactItemVariants} style={{ marginBottom: '2rem' }}>
    <p style={{
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: 'rgba(255,255,255,0.4)',
      marginBottom: '0.2rem',
    }}>
      {label}
    </p>
    {href ? (
      <a
        href={href}
        style={{
          fontSize: '1rem',
          fontWeight: 500,
          color: 'white',
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          display: 'block',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-brand)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'white')}
      >
        {value}
      </a>
    ) : (
      <p style={{ fontSize: '1rem', fontWeight: 500, color: 'white', marginBottom: 0 }}>
        {value}
      </p>
    )}
  </motion.div>
);

interface FieldGroupProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  required?: boolean;
  isTextarea?: boolean;
}

const FieldGroup: React.FC<FieldGroupProps> = ({
  label, name, type = 'text', value, onChange, required = false, isTextarea = false,
}) => (
  <div className="field-group">
    {isTextarea ? (
      <textarea
        className="field-input"
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder=" "
        rows={5}
      />
    ) : (
      <input
        className="field-input"
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder=" "
      />
    )}
    <label className="field-label">{label}</label>
  </div>
);

// ── Kontakt ───────────────────────────────────────────────────────────────────

const Kontakt: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <PageTransition>
      <div style={{ overflowX: 'hidden' }}>

        {/* ── Section 2: Split Contact ───────────────────────────── */}
        <div className="kontakt-split">

          {/* Left — dark contact info */}
          <motion.div
            className="kontakt-left"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
          >
            <motion.div variants={contactItemVariants}>
              <p style={{
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-brand)',
                marginBottom: '0.5rem',
              }}>
                KONTAKT
              </p>
              <h1 style={{
                fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
                fontWeight: 600,
                color: 'white',
                marginBottom: 0,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}>
                Kontakt & Anfahrt
              </h1>
              <div style={{
                width: '40px',
                height: '3px',
                background: 'var(--color-brand)',
                borderRadius: '2px',
                marginTop: '1rem',
                marginBottom: '3rem',
              }} />
            </motion.div>

            <ContactItem label="TELEFON" value="+43 664 2508088" href="tel:+436642508088" />
            <ContactItem label="E-MAIL" value="kontakt@cartech-dornbirn.at" href="mailto:kontakt@cartech-dornbirn.at" />
            <ContactItem
              label="ADRESSE"
              value={<>Wallenmahd 23/C3<br />6850 Dornbirn, Österreich</>}
            />
            <ContactItem
              label="ÖFFNUNGSZEITEN"
              value={<>Mo – Do: 08:00 – 12:00, 13:00 – 17:30<br />Fr: 08:00 – 12:00</>}
            />
          </motion.div>

          {/* Right — contact form */}
          <motion.div
            className="kontakt-right"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.15 }}
          >
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-text)',
              marginBottom: '2.5rem',
              letterSpacing: '-0.02em',
            }}>
              Schreiben Sie uns
            </h2>

            {showSuccess && (
              <div style={{
                padding: '1rem 1.25rem',
                backgroundColor: 'rgba(0, 150, 80, 0.08)',
                border: '1px solid rgba(0, 150, 80, 0.2)',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                fontSize: '0.9rem',
                color: '#006633',
              }}>
                Nachricht gesendet — wir melden uns bald bei Ihnen.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-row-split">
                <FieldGroup label="Name" name="name" value={formData.name} onChange={handleChange} required />
                <FieldGroup label="E-Mail" name="email" type="email" value={formData.email} onChange={handleChange} required />
              </div>
              <FieldGroup label="Telefon (optional)" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
              <FieldGroup label="Nachricht" name="message" isTextarea value={formData.message} onChange={handleChange} required />

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="submit" className="kontakt-submit-btn">
                  NACHRICHT SENDEN →
                </button>
              </div>
            </form>
          </motion.div>

        </div>

        {/* ── Section 3: Full-bleed Map ──────────────────────────── */}
        <div className="map-section">
          {isMapLoading && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#e8e8e8',
              zIndex: 1,
            }}>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>Karte wird geladen …</p>
            </div>
          )}
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3332.185698064568!2d9.7251253!3d47.3884507!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479b152a4566f889%3A0x86afa6b9345f18c0!2sCT%20Auto-Werkst%C3%A4tte%20OG%20-%20TESLA%20APPROVED%20BODYSHOP!5e0!3m2!1sde!2sat!4v1707480000000!5m2!1sde!2sat"
            width="100%"
            height="100%"
            style={{ border: 0, display: 'block', opacity: isMapLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="CarTech Dornbirn Standort"
            onLoad={() => setIsMapLoading(false)}
          />

          {/* Floating card — desktop only */}
          <motion.div
            className="map-floating-card"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--color-text)', marginBottom: '0.2rem' }}>
              CarTech Dornbirn
            </p>
            <p style={{ fontSize: '0.85rem', color: '#f5a623', marginBottom: '0.75rem', letterSpacing: '0.03em' }}>
              4.9 ★★★★★
            </p>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: '1rem' }}>
              Wallenmahd 23/C3<br />6850 Dornbirn, Österreich
            </p>
            <a
              href="https://maps.google.com/?q=Wallenmahd+23/C3+6850+Dornbirn"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'var(--color-brand)',
                textDecoration: 'none',
              }}
            >
              Route berechnen →
            </a>
          </motion.div>
        </div>

        {/* Mobile address fallback */}
        <div className="map-mobile-address">
          <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
            Wallenmahd 23/C3, 6850 Dornbirn, Österreich
          </p>
          <a
            href="https://maps.google.com/?q=Wallenmahd+23/C3+6850+Dornbirn"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-brand)', textDecoration: 'none' }}
          >
            Route berechnen →
          </a>
        </div>

      </div>

      <StickyMobileCTA />
    </PageTransition>
  );
};

export default Kontakt;
